import { Box, Dialog, DialogContent, Grid, Tab, Tabs } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import axios from 'axios';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import AddQuickReviewModal from '../../common/AddQuickReviewModal';
import { useQuickReviewUsers, useReviewRequestCards, useReviewRequestListing } from '../../services/business/reviewRequest/reviewRequest.hooks.js';
import { getCountryMasterListApi } from '../../services/core/master/master.api.js';
import { coreMasterKeys } from '../../services/core/master/master.hooks.js';
import queryClient from '../../config/queryClient';
import ReviewRequestCards from './ReviewRequestCards';
import ReviewRequestFilters from './ReviewRequestFilters';
import ReviewRequestHeader from './ReviewRequestHeader';
import ReviewRequestTable from './ReviewRequestTable';
import { CustomersListBasedOnType } from '../../redux/Invoice/action';

// ── Debounce helper ────────────────────────────────────────────────────────
function debounce(func, wait) {
    let timeout;
    return function (...args) {
        if (timeout) clearTimeout(timeout);
        timeout = setTimeout(() => func(...args), wait);
    };
}

// ── Styles ─────────────────────────────────────────────────────────────────
const styles = {
    pageBox: { bgcolor: 'background.main' },
    sectionLabel: {
        fontWeight: 500,
        fontSize: '14px',
        letterSpacing: '1px',
        borderBottom: '2px solid',
        borderColor: 'primary.main',
        pb: 1,
        mb: 3,
        display: 'inline-block',
        color: 'primary.main',
    },
    tableWrapper: { bgcolor: 'white', borderRadius: '4px' },
};

// ─────────────────────────────────────────────────────────────────────────────
const QuickReviewRequest = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const cancelTokenSourceRef = useRef(null);

    const [prefetchedCustomersPage1, setPrefetchedCustomersPage1] = useState([]);
    const [prefetchedCustomersPage1Meta, setPrefetchedCustomersPage1Meta] = useState(null);

    // ── Pagination & sorting ──────────────────────────────────────────────
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [sortBy, setSortBy] = useState('reviewRequest.requestedAt');
    const [sortType, setSortType] = useState('DSC');

    // ── Filters ───────────────────────────────────────────────────────────
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [selectDate, setSelectDate] = useState(null);
    const [dateData, setDateData] = useState('');
    const [selectedSources, setSelectedSources] = useState([]);
    const [selectedDeliveryMethods, setSelectedDeliveryMethods] = useState([]);
    const [selectedTypes, setSelectedTypes] = useState([]);

    // ── Build query params ─────────────────────────────────────────────────
    const buildParams = useCallback(
        () => ({
            page,
            limit: rowsPerPage,
            sortBy,
            sortType,
            search,
            date: dateData,
            requestedVia: selectedSources,
            deliveryMethod: selectedDeliveryMethods,
            type: selectedTypes,
        }),
        [page, rowsPerPage, sortBy, sortType, search, dateData, selectedSources, selectedDeliveryMethods, selectedTypes]
    );

    const queryParams = useMemo(() => buildParams(), [buildParams]);

    // Strip empty values
    const cleanParams = useMemo(
        () =>
            Object.fromEntries(
                Object.entries(queryParams).filter(([, v]) => v !== '' && v !== null && v !== undefined && !(Array.isArray(v) && v.length === 0))
            ),
        [queryParams]
    );

    // ── TanStack Queries ───────────────────────────────────────────────────
    const { data: cardCounts, isLoading: cardsLoading } = useReviewRequestCards({ enabled: true });
    const { data: listingResponse, isLoading: listingLoading } = useReviewRequestListing(cleanParams, { enabled: true });
    const { data: quickReviewUsers } = useQuickReviewUsers({ limit: 5 }, { enabled: true });

    // Prefetch customers + country master list on mount (so modal opens instantly)
    useEffect(() => {
        const shopCountryId = localStorage.getItem('shopCountryId') || '64e738882fec8f2c9b79c139';

        queryClient.prefetchQuery({
            queryKey: coreMasterKeys.countries({ countryId: shopCountryId }),
            queryFn: () => getCountryMasterListApi({ countryId: shopCountryId }),
        });

        // Prefetch customer list page 1 (50)
        const source = axios.CancelToken.source();
        cancelTokenSourceRef.current = source;

        dispatch(CustomersListBasedOnType('', ['shop_created', 'apex_auto_app', 'shop_imported', 'shop_guest'], 1, 50, source.token)).then((resp) => {
            if (resp?.data) {
                setPrefetchedCustomersPage1(resp.data);
                setPrefetchedCustomersPage1Meta({
                    totalPages: resp?.totalPages ?? 0,
                    currentPage: resp?.currentPage ?? 1,
                });
            }
        });

        return () => {
            if (cancelTokenSourceRef.current) cancelTokenSourceRef.current.cancel();
        };
    }, [dispatch]);

    const rows = listingResponse?.list || [];
    const totalCount = listingResponse?.totalCount || 0;
    const showLoader = listingLoading;

    // ── Handlers ───────────────────────────────────────────────────────────
    const handleChangePage = (event, newPage) => setPage(newPage + 1);

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(1);
    };

    const handleSort = (col) => {
        setSortBy(col);
        setSortType((prev) => (prev === 'DSC' ? 'ASC' : 'DSC'));
        setPage(1);
    };

    const handleDateChange = (date) => {
        setPage(1);
        if (!date) {
            setSelectDate(null);
            setDateData('');
            return;
        }
        const parsed = dayjs(date);
        if (!parsed.isValid()) return;
        setSelectDate(parsed);
        setDateData(parsed.format('YYYY-MM-DD'));
    };

    const debounceSearch = useMemo(
        () =>
            debounce((val) => {
                setSearch(val);
                setPage(1);
            }, 800),
        []
    );

    const handleSearchChange = (val) => {
        setSearchInput(val);
        debounceSearch(val);
    };

    // ── Clear filters ──────────────────────────────────────────────────────
    const hasClearableFilters =
        Boolean(dateData) ||
        (selectedSources?.length ?? 0) > 0 ||
        (selectedDeliveryMethods?.length ?? 0) > 0 ||
        (selectedTypes?.length ?? 0) > 0 ||
        Boolean(search?.trim());

    const clearFilterGroups = [
        Boolean(dateData),
        (selectedSources?.length ?? 0) > 0,
        (selectedDeliveryMethods?.length ?? 0) > 0,
        (selectedTypes?.length ?? 0) > 0,
        Boolean(search?.trim()),
    ].filter(Boolean).length;

    const clearFilterLabel = clearFilterGroups > 1 ? 'Clear Filters' : 'Clear Filter';

    const handleClearFilters = () => {
        setSelectDate(null);
        setDateData('');
        setSelectedSources([]);
        setSelectedDeliveryMethods([]);
        setSelectedTypes([]);
        setSearch('');
        setSearchInput('');
        setPage(1);
    };

    // ── Row actions ────────────────────────────────────────────────────────
    const handleViewCustomer = (row) => navigate(`/customers/${row?.customer?._id}/${row?.type || row?.customer?.type || row?.customer?.createdFrom}`);

    // ── Row navigation ─────────────────────────────────────────────────────
    const handleView = (selectedRow) => {
        if (selectedRow?.reviewRequest?.requestedFrom === 'appointment' && selectedRow?.appointment?._id) {
            const appointmentId = selectedRow?.appointment?._id;
            if (!appointmentId) return;
            // Route that exists in this app for appointment view
            navigate(`/appointment/past-appointment/${appointmentId}`);
        } else if (selectedRow?.reviewRequest?.requestedFrom === 'invoice' && (selectedRow?.invoice?._id || selectedRow?.appointment?.invoice)) {
            const invoiceId = selectedRow?.invoice?._id || selectedRow?.appointment?.invoice;
            if (!invoiceId) return;
            navigate(`/invoice/finalized/${invoiceId}`);
        } else if (selectedRow?.reviewRequest?.requestedFrom === 'quickRequest' && selectedRow?.customer?._id) {
            navigate(`/customers/${selectedRow?.customer?._id}/${selectedRow?.type || selectedRow?.customer?.type || selectedRow?.customer?.createdFrom}`);
        }
    };

    const handleViewAppointment = (row) => {
        const appointmentId = row?.appointment?._id;
        if (!appointmentId) return;
        // Route that exists in this app for appointment view
        navigate(`/appointment/past-appointment/${appointmentId}`);
    };

    const handleViewInvoice = (row) => {
        const invoiceId = row?.invoice?._id || row?.appointment?.invoice;
        if (!invoiceId) return;
        navigate(`/invoice/finalized/${invoiceId}`);
    };

    const handleNewRequest = () => setIsAddModalOpen(true);
    const handleSendFirstRequest = () => setIsAddModalOpen(true);

    // ─────────────────────────────────────────────────────────────────────
    return (
        <>
            <Box padding={'32px 24px'} sx={styles.pageBox}>
                <Grid container rowGap={3}>
                    {/* ── Header ── */}
                    <ReviewRequestHeader onNewRequest={handleNewRequest} />

                    {/* ── Stats cards ── */}
                    <ReviewRequestCards counts={cardCounts} />

                    {/* ── Request History table ── */}
                    <Grid container item>
                        <Box sx={{ width: '100%' }}>
                            {/* Section label matching Image 1 "REQUEST HISTORY" tab style */}
                            <Tabs value={0} aria-label="review request tabs">
                                <Tab label="REQUEST HISTORY" sx={{ fontWeight: 500, fontSize: '16px' }} />
                            </Tabs>

                            <Box sx={{ pt: 3 }}>
                                <Grid container item>
                                    {/* Hide filters bar when no data and no active filters */}
                                    {!(!hasClearableFilters && rows.length === 0 && !showLoader) && (
                                        <ReviewRequestFilters
                                            selectDate={selectDate}
                                            onDateChange={handleDateChange}
                                            selectedSources={selectedSources}
                                            onSourcesChange={(v) => {
                                                setSelectedSources(v);
                                                setPage(1);
                                            }}
                                            selectedDeliveryMethods={selectedDeliveryMethods}
                                            onDeliveryMethodsChange={(v) => {
                                                setSelectedDeliveryMethods(v);
                                                setPage(1);
                                            }}
                                            selectedTypes={selectedTypes}
                                            onTypesChange={(v) => {
                                                setSelectedTypes(v);
                                                setPage(1);
                                            }}
                                            search={searchInput}
                                            onSearchChange={handleSearchChange}
                                            hasClearableFilters={hasClearableFilters}
                                            clearFilterLabel={clearFilterLabel}
                                            onClearFilters={handleClearFilters}
                                        />
                                    )}

                                    <ReviewRequestTable
                                        filterApplied={hasClearableFilters}
                                        rows={rows}
                                        showLoader={showLoader}
                                        totalCount={totalCount}
                                        page={page}
                                        rowsPerPage={rowsPerPage}
                                        sortBy={sortBy}
                                        sortType={sortType}
                                        onSort={handleSort}
                                        onPageChange={handleChangePage}
                                        onRowsPerPageChange={handleChangeRowsPerPage}
                                        onViewCustomer={handleViewCustomer}
                                        onViewAppointment={handleViewAppointment}
                                        onViewInvoice={handleViewInvoice}
                                        onSendFirstRequest={handleSendFirstRequest}
                                        onRowClick={handleView}
                                    />
                                </Grid>
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
            </Box>

            {/* ── Cards loading overlay ── */}
            <Dialog open={cardsLoading || listingLoading}>
                <DialogContent>
                    <CircularProgress />
                </DialogContent>
            </Dialog>

            <AddQuickReviewModal
                open={isAddModalOpen}
                onClose={() => setIsAddModalOpen(false)}
                prefetchedQuickReviewUsers={quickReviewUsers}
                prefetchedCustomersPage1={prefetchedCustomersPage1}
                prefetchedCustomersPage1Meta={prefetchedCustomersPage1Meta}
            />
        </>
    );
};

export default QuickReviewRequest;
